import { useEffect, useMemo, useState } from 'react';
import {
  ApiError,
  AssignmentRecord,
  Department,
  InventoryRadio,
  Tenant,
  assignRadios,
  getDepartments,
  getInventory,
  getRadioHistory,
  getTenants,
  returnRadios,
} from '../../services/tenancy';

// Rental inventory: physical radios are platform inventory, temporarily assigned to a customer's
// network. Assign/return are bulk, all-or-nothing server operations (documentation/protocol.md).

const STATE_STYLE: Record<string, string> = {
  AVAILABLE: 'bg-green-900 text-green-300',
  ASSIGNED: 'bg-blue-900 text-blue-300',
  PENDING_APPROVAL: 'bg-yellow-900 text-yellow-300',
  REJECTED: 'bg-red-900 text-red-300',
};

function formatTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('da-DK', { dateStyle: 'short', timeStyle: 'short', hour12: false }).format(new Date(value));
}

function errorText(err: unknown, radios: InventoryRadio[]): string {
  if (err instanceof ApiError) {
    const labels = err.ids.map((id) => radios.find((r) => r.id === id)?.radio_id || id);
    return labels.length ? `${err.message}: ${labels.join(', ')}` : err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

export default function InventoryManager() {
  const [radios, setRadios] = useState<InventoryRadio[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [stateFilter, setStateFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Assign dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [targetTenant, setTargetTenant] = useState('');
  const [targetNetwork, setTargetNetwork] = useState('');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [targetDepartments, setTargetDepartments] = useState<Set<string>>(new Set());
  const [reference, setReference] = useState('');

  // History panel
  const [historyFor, setHistoryFor] = useState<InventoryRadio | null>(null);
  const [history, setHistory] = useState<AssignmentRecord[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const [inv, ten] = await Promise.all([getInventory({ state: stateFilter, tenant_id: tenantFilter }), getTenants()]);
      setRadios(inv);
      setTenants(ten);
      setSelected((prev) => new Set([...prev].filter((id) => inv.some((r) => r.id === id))));
      setError(null);
    } catch (err) {
      setError(errorText(err, []));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter, tenantFilter]);

  useEffect(() => {
    setTargetNetwork('');
    setTargetDepartments(new Set());
    setDepartments([]);
    if (targetTenant) getDepartments(targetTenant).then(setDepartments).catch(() => setDepartments([]));
  }, [targetTenant]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return radios;
    return radios.filter((r) =>
      [r.radio_id, r.name, r.model, r.serial_number, r.network_name, r.tenant_name, r.reference].some((v) => v?.toLowerCase().includes(q)),
    );
  }, [radios, search]);

  const selectedRadios = radios.filter((r) => selected.has(r.id));
  const selectedAvailable = selectedRadios.filter((r) => r.state === 'AVAILABLE');
  const selectedAssigned = selectedRadios.filter((r) => r.state === 'ASSIGNED');
  const networksOfTarget = tenants.find((t) => t.id === targetTenant)?.networks || [];

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allVisibleSelected = visible.length > 0 && visible.every((r) => selected.has(r.id));
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      visible.forEach((r) => (allVisibleSelected ? next.delete(r.id) : next.add(r.id)));
      return next;
    });

  const doAssign = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await assignRadios(selectedAvailable.map((r) => r.id), targetNetwork, [...targetDepartments], reference);
      setNotice(`Assigned ${res.changed.length} radio(s) to ${res.network?.tenant_name} / ${res.network?.name}` +
        (res.unchanged.length ? ` (${res.unchanged.length} already there)` : ''));
      setAssignOpen(false);
      setReference('');
      setSelected(new Set());
      await load();
    } catch (err) {
      setError(errorText(err, radios));
    } finally {
      setBusy(false);
    }
  };

  const doReturn = async () => {
    const labels = selectedAssigned.map((r) => r.radio_id).join(', ');
    if (!window.confirm(`Return ${selectedAssigned.length} radio(s) to available inventory?\n\n${labels}\n\n` +
      'Every customer-specific setting is reset: groups, departments, SOS recipients, button actions, name and radio settings.')) return;
    setBusy(true);
    setError(null);
    try {
      const res = await returnRadios(selectedAssigned.map((r) => r.id));
      setNotice(`Returned ${res.changed.length} radio(s) to inventory`);
      setSelected(new Set());
      await load();
    } catch (err) {
      setError(errorText(err, radios));
    } finally {
      setBusy(false);
    }
  };

  const openHistory = async (radio: InventoryRadio) => {
    setHistoryFor(radio);
    setHistory([]);
    try {
      setHistory(await getRadioHistory(radio.id));
    } catch (err) {
      setError(errorText(err, radios));
    }
  };

  const counts = radios.reduce<Record<string, number>>((acc, r) => ({ ...acc, [r.state]: (acc[r.state] || 0) + 1 }), {});

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold">Rental Inventory</h2>
        <p className="text-sm text-gray-400">
          Physical radios belong to the platform. Assign available radios to a customer network and return them when the rental ends;
          a return resets everything customer-specific. Departments are optional.
        </p>
      </div>

      {error && <div className="p-3 rounded bg-red-900/50 border border-red-700 text-red-200 text-sm">{error}</div>}
      {notice && (
        <div className="p-3 rounded bg-green-900/40 border border-green-700 text-green-200 text-sm flex justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-green-300 hover:text-white" aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm">
          <option value="">All states</option>
          <option value="AVAILABLE">Available</option>
          <option value="ASSIGNED">Assigned</option>
          <option value="PENDING_APPROVAL">Pending approval</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)} className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm">
          <option value="">All customers</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search radio, serial, network…"
          className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm flex-1 min-w-[12rem]" />
        <span className="text-xs text-gray-400">
          {['AVAILABLE', 'ASSIGNED', 'PENDING_APPROVAL', 'REJECTED'].filter((s) => counts[s]).map((s) => `${counts[s]} ${s.toLowerCase().replace('_', ' ')}`).join(' · ')}
        </span>
      </div>

      <div className="flex flex-wrap gap-2 items-center bg-gray-800 border border-gray-700 rounded p-3">
        <span className="text-sm text-gray-300">{selected.size} selected</span>
        <button disabled={busy || selectedAvailable.length === 0} onClick={() => setAssignOpen(true)}
          className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-sm font-medium">
          Assign {selectedAvailable.length || ''} available → network
        </button>
        <button disabled={busy || selectedAssigned.length === 0} onClick={doReturn}
          className="px-3 py-1.5 rounded bg-orange-600 hover:bg-orange-500 disabled:opacity-40 text-sm font-medium">
          Return {selectedAssigned.length || ''} assigned → inventory
        </button>
        {selected.size > 0 && (
          <button onClick={() => setSelected(new Set())} className="px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600 text-sm">Clear selection</button>
        )}
      </div>

      <div className="bg-gray-800 rounded border border-gray-700 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-900 text-gray-400">
            <tr>
              <th className="px-3 py-2"><input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} aria-label="Select all visible" /></th>
              <th className="px-3 py-2">Radio</th>
              <th className="px-3 py-2">Model / serial</th>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2">Customer / network</th>
              <th className="px-3 py-2">Since</th>
              <th className="px-3 py-2">Reference</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">Loading…</td></tr>}
            {!loading && visible.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">No radios</td></tr>}
            {!loading && visible.map((r) => (
              <tr key={r.id} className="border-t border-gray-700 hover:bg-gray-700/50">
                <td className="px-3 py-2"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} aria-label={`Select ${r.radio_id}`} /></td>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.radio_id}</div>
                  {r.name && r.name !== r.radio_id && <div className="text-xs text-gray-400">{r.name}</div>}
                </td>
                <td className="px-3 py-2"><div>{r.model || '—'}</div><div className="text-xs text-gray-500">{r.serial_number}</div></td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs ${STATE_STYLE[r.state] || ''}`}>{r.state.replace('_', ' ')}</span></td>
                <td className="px-3 py-2">{r.network_name ? <><div>{r.tenant_name}</div><div className="text-xs text-gray-400">{r.network_name}</div></> : '—'}</td>
                <td className="px-3 py-2 text-gray-300">{formatTime(r.assigned_at)}</td>
                <td className="px-3 py-2 text-gray-300">{r.reference || '—'}</td>
                <td className="px-3 py-2"><button onClick={() => openHistory(r)} className="text-blue-400 hover:text-blue-300 text-xs">History</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {assignOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="bg-gray-800 border border-gray-600 rounded-lg w-full max-w-lg p-5 space-y-4">
            <h3 className="text-lg font-bold">Assign {selectedAvailable.length} radio(s)</h3>
            <p className="text-xs text-gray-400 break-words">{selectedAvailable.map((r) => r.radio_id).join(', ')}</p>
            <label className="block text-sm">
              Customer
              <select value={targetTenant} onChange={(e) => setTargetTenant(e.target.value)} className="mt-1 w-full bg-gray-900 border border-gray-600 rounded px-3 py-2">
                <option value="">Select customer…</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              Network
              <select value={targetNetwork} onChange={(e) => setTargetNetwork(e.target.value)} disabled={!targetTenant}
                className="mt-1 w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 disabled:opacity-50">
                <option value="">Select network…</option>
                {networksOfTarget.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
              </select>
            </label>
            {departments.length > 0 && (
              <fieldset className="text-sm">
                <legend className="mb-1">Departments <span className="text-gray-400">(optional)</span></legend>
                <div className="flex flex-wrap gap-3">
                  {departments.map((d) => (
                    <label key={d.id} className="flex items-center gap-1">
                      <input type="checkbox" checked={targetDepartments.has(d.id)} onChange={() => setTargetDepartments((prev) => {
                        const next = new Set(prev);
                        if (next.has(d.id)) next.delete(d.id);
                        else next.add(d.id);
                        return next;
                      })} />
                      {d.name}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
            <label className="block text-sm">
              Reference <span className="text-gray-400">(optional, e.g. rental order)</span>
              <input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200}
                className="mt-1 w-full bg-gray-900 border border-gray-600 rounded px-3 py-2" />
            </label>
            {error && <div className="text-sm text-red-300">{error}</div>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setAssignOpen(false)} className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-sm">Cancel</button>
              <button onClick={doAssign} disabled={busy || !targetNetwork}
                className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-sm font-medium">
                {busy ? 'Assigning…' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {historyFor && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="bg-gray-800 border border-gray-600 rounded-lg w-full max-w-3xl p-5 space-y-3 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold">Rental history – {historyFor.radio_id}</h3>
              <button onClick={() => setHistoryFor(null)} className="text-gray-400 hover:text-white" aria-label="Close">✕</button>
            </div>
            {history.length === 0 ? <p className="text-sm text-gray-400">No assignments recorded.</p> : (
              <table className="w-full text-sm text-left">
                <thead className="text-gray-400">
                  <tr><th className="py-1 pr-3">Customer / network</th><th className="py-1 pr-3">Assigned</th><th className="py-1 pr-3">Returned</th><th className="py-1 pr-3">Reference</th><th className="py-1">Reset on return</th></tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-t border-gray-700 align-top">
                      <td className="py-1 pr-3"><div>{h.tenant_name}</div><div className="text-xs text-gray-400">{h.network_name}</div></td>
                      <td className="py-1 pr-3">{formatTime(h.assigned_at)}<div className="text-xs text-gray-500">{h.assigned_via}</div></td>
                      <td className="py-1 pr-3">{h.unassigned_at ? <>{formatTime(h.unassigned_at)}<div className="text-xs text-gray-500">{h.unassigned_via}</div></> : <span className="text-blue-300">current</span>}</td>
                      <td className="py-1 pr-3">{h.reference || '—'}</td>
                      <td className="py-1 text-xs text-gray-300">
                        {h.return_summary ? Object.entries(h.return_summary).map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`).join(', ') || 'settings only' : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
